import { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import useVerifyProperty from '../../hooks/useVerifyProperty';

interface Props {
  property_id: string;
  is_validated_by_proper: boolean;
}

// Aviso compacto: verificar publica el check "Verificado" en la ficha de la web, así que queda
// visible arriba de la pantalla pero sin ocupar espacio (la confirmación va en el modal).
const VerifyPropertyBanner = ({
  property_id,
  is_validated_by_proper,
}: Props) => {
  const { isVerified, isSaving, changeVerified, setIsVerified } =
    useVerifyProperty(property_id, is_validated_by_proper);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Si la pantalla recarga la propiedad, el valor del servidor manda.
  useEffect(() => {
    setIsVerified(is_validated_by_proper);
  }, [is_validated_by_proper]);

  const confirm = async () => {
    await changeVerified(!isVerified);
    setIsModalOpen(false);
  };

  const color = isVerified ? 'success' : 'warning';

  return (
    <>
      {/* Aviso discreto: una sola fila, sin sombra, con un borde lateral del color del estado. */}
      <div
        className="d-flex align-items-center flex-wrap rounded px-3 py-2 mb-3"
        style={{
          background: isVerified ? '#f1fbf6' : '#fffaf0',
          border: '1px solid #e9ecef',
          borderLeft: `4px solid ${isVerified ? '#2dce89' : '#fb6340'}`,
        }}
        role="region"
        aria-label="Verificación de la propiedad"
      >
        <i
          className={`fa-solid ${
            isVerified
              ? 'fa-circle-check text-success'
              : 'fa-triangle-exclamation text-warning'
          } mr-3`}
          style={{ fontSize: '1.1rem' }}
          aria-hidden="true"
        />
        <div className="flex-grow-1 mr-3" style={{ minWidth: 220 }}>
          <span className={`font-weight-bold text-${color}`}>
            {isVerified
              ? 'Propiedad verificada'
              : 'Propiedad sin verificar'}
          </span>
          <small className="d-block text-muted">
            {isVerified
              ? 'El check "Verificado" se muestra en la ficha de la web.'
              : 'Verifícala solo después de confirmar con el propietario que los datos y las fotos son reales.'}
          </small>
        </div>
        <Button
          type="button"
          size="sm"
          color={isVerified ? 'secondary' : 'success'}
          outline={isVerified}
          disabled={isSaving}
          onClick={() => setIsModalOpen(true)}
        >
          {isVerified ? 'Quitar verificación' : 'Verificar'}
        </Button>
      </div>

      <Modal
        isOpen={isModalOpen}
        toggle={() => !isSaving && setIsModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !isSaving && setIsModalOpen(false)}>
          {isVerified ? 'Quitar verificación' : 'Verificar propiedad'}
        </ModalHeader>
        <ModalBody>
          {isVerified
            ? 'Se dejará de mostrar el check "Verificado" en la ficha de la web. ¿Quieres continuar?'
            : 'Se mostrará públicamente el check "Verificado" en la ficha de la web. ¿Ya confirmaste con el propietario que los datos y las fotos son reales?'}
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="secondary"
            outline
            disabled={isSaving}
            onClick={() => setIsModalOpen(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            color={isVerified ? 'danger' : 'success'}
            disabled={isSaving}
            onClick={confirm}
          >
            {isSaving
              ? 'Guardando...'
              : isVerified
                ? 'Sí, quitar'
                : 'Sí, verificar'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default VerifyPropertyBanner;
